import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    HeartHandshake,
    Sparkles,
    Camera,
    Wine,
    Layers,
    Smile,
    Sliders,
    Clock,
    Heart,
    Moon,
    Users,
    Image as ImageIcon
} from 'lucide-react';

const weddingServices = [
    { id: '01', icon: HeartHandshake, title: 'Documentary Ceremony Coverage', desc: 'Capturing the raw, unscripted emotional highs—from the tearful first looks to the electric energy of your walk down the aisle.' },
    { id: '02', icon: Camera, title: 'Editorial Couple Portraits', desc: 'Blended high-fashion editorial composition with intimate, natural connection, turning quiet moments alone into timeless works of art.' },
    { id: '03', icon: Wine, title: 'Candid Cocktail & Reception Stories', desc: 'Spontaneous, high-energy photojournalism documenting the laughter, moving toasts, and uninhibited movement on the dance floor.' },
    { id: '04', icon: Sparkles, title: 'Luxury Detail & Venue Curation', desc: 'Meticulously styling and framing your invitations, rings, intricate floral design, and the raw architectural ambiance of your venue.' },
    { id: '05', icon: Users, title: 'Intimate Bridal Party & Family Stills', desc: 'Editorial group framing that feels sophisticated and natural, moving far away from stiff, boring, and clinical traditional lineups.' },
    { id: '06', icon: Moon, title: 'Flash-Driven Night & Grand Exits', desc: 'Vibrant, cinematic direct-flash and ambient low-light imagery capturing sparkler exits, late-night dancing, and car departures.' },
];

const weddingDeliverables = [
    { id: '01', icon: Clock, title: 'Timeline & Daylight Coordination', desc: 'Collaborating directly with your planner to map out custom lighting windows, ensuring perfect golden-hour portraits.' },
    { id: '02', icon: Sliders, title: 'Dual-Slot Real-Time Image Backup', desc: 'Every shutter click is written natively onto duplicate, separate memory cards on-set to guarantee total asset preservation.' },
    { id: '03', icon: Heart, title: 'Unobtrusive Photojournalistic Approach', desc: 'Melding into the background during crucial moments to capture raw, authentic chemistry without breaking the natural event flow.' },
    { id: '04', icon: Layers, title: 'Cohesive Editorial Color Grading', desc: 'Hand-editing every single gallery frame to deliver a timeless, cinematic color profile that will never look dated.' },
    { id: '05', icon: Sparkles, title: 'Next-Day Sneak Peek Deliveries', desc: 'Deploying a beautiful, curated suite of high-resolution highlights within 24 hours so you can instantly share your memories.' },
    { id: '06', icon: Camera, title: 'Multi-Lens Dynamic Focal Depth', desc: 'Utilizing a suite of luxury prime lenses to shift instantly from vast, sweeping environmental architecture to intimate, creamy close-ups.' },
    { id: '07', icon: Smile, title: 'Comfort-First Couple Direction', desc: 'Swapping awkward, complex poses for interactive prompts that make you feel entirely like yourselves, laughing naturally together.' },
    { id: '08', icon: Wine, title: 'Rehearsal Dinner Coverage Add-ons', desc: 'Documenting the warm, casual welcome toasts and initial guest arrivals to give your weekend story a complete visual narrative.' },
    { id: '09', icon: ImageIcon, title: 'Redundant Digital Master Archives', desc: 'Securing your high-resolution uncompressed master assets across premium cloud servers for lifetime print and album security.' },
];

const WeddingPhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.55;
        const speedRight = 0.35;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Wedding Photography Portfolio in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Explore the Wedding Photography Portfolio of KhultStudios. Discover candid wedding photography, traditional wedding photography, pre-wedding shoots, engagement sessions, bridal portraits, cinematic wedding films, and unforgettable wedding moments captured across Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Wedding Photography Portfolio Hyderabad, Wedding Photographer Hyderabad, Candid Wedding Photography, Traditional Wedding Photography, Bridal Photography, Groom Photography, Wedding Photoshoot, Pre Wedding Portfolio, Engagement Photography, Reception Photography, Wedding Videography, Cinematic Wedding Films, Destination Wedding Photography, Best Wedding Photographer Hyderabad, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/wedding-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/wedding-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Wedding Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Browse our wedding photography portfolio featuring candid moments, bridal portraits, pre-wedding shoots, engagement ceremonies, receptions, and cinematic wedding storytelling."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/wedding-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Wedding Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="View our portfolio of wedding photography, pre-wedding shoots, bridal portraits, and cinematic wedding films in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/wedding-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/wedding_cinematic_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Editorial & Documentary Wedding Visuals
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Stories Told with Artistry & Intent
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Your wedding day shouldn’t feel like an endless, exhausting photoshoot. The real magic isn't manufactured—it lives in the nervous squeeze of a hand, a quiet glance before the doors open, and the unfiltered chaos of an electric dance floor.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we balance sophisticated fashion-forward editorial curation with raw, honest documentary storytelling. By tailoring stress-free daylight timelines, preserving organic grain textures, and keeping you entirely present, we craft an heirloom gallery that feels uniquely yours.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        View Collections
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=500" alt="Cinematic Editorial Couple Portrait Outdoor" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=500" alt="Candid Emotional Ceremony Moment Groom Reacting" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1532712938310-34cb3982ef74?q=80&w=500" alt="Luxury Wedding Tablescape Detail Design Frame" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1606800052052-a08af7148866?q=80&w=500" alt="Dynamic High Energy Reception Dance Floor Capture" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=500" alt="Cinematic Editorial Couple Portrait Outdoor Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=500" alt="Candid Emotional Ceremony Moment Groom Reacting Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1532712938310-34cb3982ef74?q=80&w=500" alt="Luxury Wedding Tablescape Detail Design Frame Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1606800052052-a08af7148866?q=80&w=500" alt="Dynamic High Energy Reception Dance Floor Capture Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/wedding-photography/wedding-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* WEDDING SERVICES DESCRIPTION SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">✨ Tailored Wedding Storytelling Modules</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Every wedding weekend moves to its own organic rhythm. Our specialized modules are intentionally designed to preserve both the curated luxury design and the fast-moving emotional highlights seamlessly.
                    </p>
                    <div className="row g-4 mt-2">
                        {weddingServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TECHNICAL EXECUTION & PROCESS SECTION */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Safeguards & On-Set Supporting Frameworks</h2>
                    <div className="row g-4">
                        {weddingDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WeddingPhotographyPortfolio;